import { Controller } from "@hotwired/stimulus"

import { type ImageSlide } from "@/helpers/search_map"

export default class MapMarkerCarouselController extends Controller {
  static values = { id: String, image: Object }
  declare readonly idValue: string
  declare readonly imageValue: ImageSlide

  connect(): void {
    this.element.outerHTML = this.carouselView

    void (async (): Promise<void> => {
      await this.requestCarouselImages()
    })()
  }

  private appendCarouselImages(images: ImageSlide[]): void {
    const carousel = document.getElementById(this.carouselId) as HTMLUListElement

    carousel.insertAdjacentHTML(
      "beforeend",
      images.map((image, index) => this.slideListItem(image, index + 1)).join("")
    )

    Array.from(carousel.children).forEach((child) => {
      child.setAttribute("aria-setsize", (images.length + 1).toString())
    })
  }

  private async requestCarouselImages(): Promise<void> {
    try {
      const result = await fetch(`/search/carousel_images?venue_id=${this.idValue}`)
      const images = (await result.json()) as ImageSlide[]

      this.appendCarouselImages(images)
    } catch (_) {
      // Ignore fetch errors
    }
  }

  private get carouselId(): string {
    return `carousel-search-map-${this.idValue}`
  }

  private get carouselName(): string {
    return "Search Map"
  }

  private get carouselView(): string {
    return `
      <div
        class="ui-component-carousel variant:map-marker"
        data-controller="carousel"
        data-carousel-name-value="${this.carouselName}"
      >
        <div>
          <ol
            id="${this.carouselId}"
            aria-live="polite"
            tabindex="0"
            data-carousel-target="slideList"
          >
            ${this.slideListItem(this.imageValue, 0)}
          </ol>

          <button
            type="button"
            aria-label="Previous slide"
            aria-controls="${this.carouselId}"
            data-action="click->carousel#slidePrev"
            data-carousel-target="prevButton"
            data-ga4-click="carousel_click"
            data-ga4-event-group="${this.carouselName}"
            data-ga4-click-target="Previous"
            aria-disabled="true"
          >
            <i class="fas fa-circle-chevron-left" aria-hidden="true"></i>
          </button>
          <button
            type="button"
            aria-label="Next slide"
            aria-controls="${this.carouselId}"
            data-action="click->carousel#slideNext"
            data-carousel-target="nextButton"
            data-ga4-click="carousel_click"
            data-ga4-event-group="${this.carouselName}"
            data-ga4-click-target="Next"
          >
            <i class="fa fa-circle-chevron-right" aria-hidden="true"></i>
          </button>
        </div>
      </div>
    `
  }

  private pictureTag({
    imgAlt,
    imgHeight,
    imgSrc,
    imgWidth,
    srcset1x,
    srcset2x,
    srcsizes
  }: ImageSlide): string {
    return `
      <picture>
        <source
          media="(min-resolution: 1.5x)"
          srcset="${srcset2x}"
          sizes="${srcsizes}"
        >
        <source srcset="${srcset1x}">
        <img
          src="${imgSrc}"
          width="${imgWidth}"
          height="${imgHeight}"
          alt="${imgAlt}"
          fetchpriority="auto"
          class="h-full object-cover"
          loading="lazy"
        >
      </picture>
    `
  }

  private slideListItem(image: ImageSlide, index: number): string {
    const position = index + 1

    return `
      <li
        aria-roledescription="slide"
        aria-posinset="${position}"
        aria-setsize="1"
        aria-label="Slide ${position}"
        aria-hidden="${position > 1 ? "true" : "false"}"
        data-carousel-target="slideListItem"
        ${position > 2 ? "hidden" : ""}
      >
        ${this.pictureTag(image)}
      </li>
    `
  }
}
